import React, { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import toast from "react-hot-toast";
import {
  adminCameraIsExternal,
  adminNodeListCameras,
} from "../../api/adminNodeCameras.ts";
import {
  CAMERA_ASSET_HOTSPOT_SHOW_TYPES,
  HOTSPOT_TEXT_MAX_CHARS,
  getHotspotShowTypeLabel,
  apiUrlHotspotEntriesFromContent,
  serializeApiUrlHotspotEntries,
  validateApiUrlHotspotEntries,
  type ApiUrlHotspotEntry,
  deleteCameraHotspot,
  fetchCameraHotspots,
  hasServerHotspotContent,
  saveCameraHotspot,
  validateHotspotUploadFile,
  HOTSPOT_IMAGE_MAX_BYTES,
  HOTSPOT_PDF_MAX_BYTES,
  type CameraAssetHotspot,
  type CameraAssetHotspotInput,
  type CameraAssetHotspotShowType,
} from "../../api/cameraHotspots.ts";
import { AdminApiUrlHotspotFields } from "./AdminApiUrlHotspotFields.tsx";
import {
  fetchCameraPresets,
  saveCameraPreset,
  type CameraPreset,
} from "../../api/ptzPresets.ts";
import { PRESET_GRID_COLUMNS, PRESET_GRID_ROWS, PRESET_GRID_TOTAL } from "../../lib/presetGrid.ts";
import { CameraHotspotAnglePicker } from "./CameraHotspotAnglePicker.tsx";
import DeletePatrolDrawingConfirmDialog from "./patrolAdmin/DeletePatrolDrawingConfirmDialog.jsx";

const EMPTY_FORM: CameraAssetHotspotInput = {
  yaw_deg: 0,
  pitch_deg: 0,
  pin_direction_deg: 0,
  asset_name: "",
  show_type: "image",
  content_url: "",
  image_url: "",
};

const SHOW_TYPE_HINTS: Record<CameraAssetHotspotShowType, string> = {
  image: `Upload an image file (JPG, PNG, WEBP, max ${HOTSPOT_IMAGE_MAX_BYTES / (1024 * 1024)}MB).`,
  pdf: `Upload a PDF file (max ${HOTSPOT_PDF_MAX_BYTES / (1024 * 1024)}MB).`,
  website: "Full https URL to open in the patrol viewer.",
  youtube: "YouTube watch or youtu.be link.",
  camera: "Link to an external camera feed shown in the patrol viewer.",
  text: `Plain text shown in the patrol viewer (max ${HOTSPOT_TEXT_MAX_CHARS} characters).`,
  api_url:
    "Add API URLs with a signal type (green, red, or yellow). Patrol shows matching buttons on the hotspot.",
};

const SHOW_TYPE_LABELS: Record<CameraAssetHotspotShowType, string> = {
  image: "Image",
  pdf: "PDF",
  website: "Website",
  youtube: "YouTube",
  camera: "Camera",
  text: "Text",
  api_url: "API URL",
};

function formatSavedHotspotType(showType: CameraAssetHotspotShowType): string {
  return getHotspotShowTypeLabel(showType);
}

type ExternalCameraOption = { id: string; name: string };
type HotspotDialogMode = "hotspot" | "preset";

const DEFAULT_PRESET_GRID_IDS = Array.from(
  { length: PRESET_GRID_TOTAL },
  (_, idx) => idx + 1,
) as number[];
const PRESET_ID_MIN = 1;
const PRESET_ID_MAX = 255;

function acceptsForShowType(showType: CameraAssetHotspotShowType): string {
  if (showType === "pdf") return ".pdf,application/pdf";
  if (showType === "image") return ".jpg,.jpeg,.png,.webp,.gif,image/*";
  return "";
}

function HotspotFileUploadField({
  showType,
  uploadFile,
  uploadPreviewUrl,
  hasStoredUpload,
  onPickFile,
  onClearFile,
}: {
  showType: CameraAssetHotspotShowType;
  uploadFile: File | null;
  uploadPreviewUrl: string | null;
  hasStoredUpload?: boolean;
  onPickFile: (file: File) => void;
  onClearFile: () => void;
}) {
  if (showType !== "image" && showType !== "pdf") return null;

  const inputId = `camera-hotspot-upload-${showType}`;

  return (
    <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#364153", mb: 0.75 }}>
        Upload {showType === "pdf" ? "PDF" : "image"}
      </Typography>
      <Box
        sx={{
          border: "1.5px dashed #CBD5E1",
          borderRadius: "10px",
          p: 2,
          textAlign: "center",
          bgcolor: "#F9FAFB",
          cursor: "pointer",
        }}
        onClick={() => document.getElementById(inputId)?.click()}
      >
        <CloudUploadIcon sx={{ fontSize: 36, color: "#94A3B8", mb: 0.5 }} />
        <Typography sx={{ fontSize: 13, color: "#64748B" }}>
          Click to upload or drag and drop
        </Typography>
        <Typography sx={{ fontSize: 11, color: "#94A3B8" }}>
          {showType === "pdf"
            ? `PDF, max ${HOTSPOT_PDF_MAX_BYTES / (1024 * 1024)}MB`
            : `JPG, PNG, WEBP, max ${HOTSPOT_IMAGE_MAX_BYTES / (1024 * 1024)}MB`}
        </Typography>
        <input
          id={inputId}
          type="file"
          hidden
          accept={acceptsForShowType(showType)}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onPickFile(file);
            e.target.value = "";
          }}
        />
      </Box>
      {uploadFile ? (
        <Box
          sx={{
            mt: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography sx={{ fontSize: 12, color: "#374151", flex: 1 }}>
            {uploadFile.name} ({(uploadFile.size / 1024).toFixed(0)} KB)
          </Typography>
          <Button
            size="small"
            color="error"
            onClick={onClearFile}
            sx={{ textTransform: "none" }}
          >
            Remove file
          </Button>
        </Box>
      ) : null}
      {showType === "image" && uploadPreviewUrl ? (
        <Box
          component="img"
          src={uploadPreviewUrl}
          alt="Upload preview"
          sx={{
            mt: 1,
            maxWidth: "100%",
            maxHeight: 140,
            objectFit: "contain",
            borderRadius: 1,
            border: "1px solid #E5E7EB",
          }}
        />
      ) : null}
      {!uploadFile && hasStoredUpload ? (
        <Typography sx={{ fontSize: 12, color: "#059669", mt: 1 }}>
          File saved on server. Upload a new file to replace it.
        </Typography>
      ) : null}
    </Box>
  );
}

function hotspotToForm(h: CameraAssetHotspot): CameraAssetHotspotInput {
  return {
    yaw_deg: h.yaw_deg,
    pitch_deg: h.pitch_deg,
    pin_direction_deg: h.pin_direction_deg,
    asset_name: h.asset_name,
    show_type: h.show_type,
    content_url: h.content_url,
    image_url: h.image_url ?? "",
  };
}

type AdminCameraHotspotDialogProps = {
  open: boolean;
  onClose: () => void;
  cameraId: string;
  cameraName?: string;
  /** True when the camera has a 360° stream URL configured. */
  has360Stream?: boolean;
  /** True when the camera has a PTZ stream URL configured. */
  hasPtzStream?: boolean;
  initialMode?: HotspotDialogMode;
};

export function AdminCameraHotspotDialog({
  open,
  onClose,
  cameraId,
  cameraName = "",
  has360Stream = false,
  hasPtzStream = false,
  initialMode = "hotspot",
}: AdminCameraHotspotDialogProps) {
  const [mode, setMode] = useState<HotspotDialogMode>("hotspot");
  const [hotspots, setHotspots] = useState<CameraAssetHotspot[]>([]);
  const [loadingHotspots, setLoadingHotspots] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CameraAssetHotspotInput>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadPreviewUrl, setUploadPreviewUrl] = useState<string | null>(null);
  const [hasStoredUpload, setHasStoredUpload] = useState(false);
  const [externalCameras, setExternalCameras] = useState<ExternalCameraOption[]>(
    [],
  );
  const [loadingExternalCameras, setLoadingExternalCameras] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CameraAssetHotspot | null>(
    null,
  );
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [presetId, setPresetId] = useState("");
  const [selectedGridId, setSelectedGridId] = useState<number | null>(null);
  const [cameraPresets, setCameraPresets] = useState<CameraPreset[]>([]);
  const [loadingPresets, setLoadingPresets] = useState(false);
  const [savingPreset, setSavingPreset] = useState(false);
  const [apiUrlEntries, setApiUrlEntries] = useState<ApiUrlHotspotEntry[]>(() =>
    apiUrlHotspotEntriesFromContent(""),
  );

  const clearApiUrlFields = useCallback(() => {
    setApiUrlEntries(apiUrlHotspotEntriesFromContent(""));
  }, []);

  const reload = useCallback(async () => {
    if (!cameraId) {
      setHotspots([]);
      return;
    }
    setLoadingHotspots(true);
    try {
      const list = await fetchCameraHotspots(cameraId);
      setHotspots(list);
    } catch (e) {
      setHotspots([]);
      toast.error(
        e instanceof Error ? e.message : "Could not load hotspots.",
      );
    } finally {
      setLoadingHotspots(false);
    }
  }, [cameraId]);

  const reloadPresets = useCallback(async () => {
    if (!cameraId) {
      setCameraPresets([]);
      return;
    }
    setLoadingPresets(true);
    try {
      const list = await fetchCameraPresets(cameraId);
      setCameraPresets(list);
    } catch (e) {
      setCameraPresets([]);
      toast.error(e instanceof Error ? e.message : "Could not load presets.");
    } finally {
      setLoadingPresets(false);
    }
  }, [cameraId]);

  useEffect(() => {
    if (!open) return;
    setMode(initialMode);
    void reload();
    void reloadPresets();
    setEditingId(null);
    setForm(EMPTY_FORM);
    setUploadFile(null);
    setUploadPreviewUrl(null);
    setHasStoredUpload(false);
    setPresetId("");
    setSelectedGridId(null);
  }, [cameraId, initialMode, open, reload, reloadPresets]);

  useEffect(() => {
    if (!open) {
      setExternalCameras([]);
      return;
    }
    let cancelled = false;
    setLoadingExternalCameras(true);
    void adminNodeListCameras()
      .then((rows) => {
        if (cancelled) return;
        const options = rows
          .filter(adminCameraIsExternal)
          .map((row) => {
            const id = String(row.id ?? row.camera_id ?? "").trim();
            const name = String(row.name ?? "").trim() || id || "Camera";
            return { id, name };
          })
          .filter((c) => c.id)
          .sort((a, b) => a.name.localeCompare(b.name));
        setExternalCameras(options);
      })
      .catch(() => {
        if (!cancelled) setExternalCameras([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingExternalCameras(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!uploadFile) {
      setUploadPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(uploadFile);
    setUploadPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [uploadFile]);

  const clearUpload = useCallback(() => {
    setUploadFile(null);
    setHasStoredUpload(false);
  }, []);

  const handlePickFile = useCallback(
    (file: File) => {
      const st = form.show_type;
      if (st !== "image" && st !== "pdf") return;
      const err = validateHotspotUploadFile(file, st);
      if (err) {
        toast.error(err);
        return;
      }
      setUploadFile(file);
      setHasStoredUpload(false);
      setForm((p) => ({ ...p, content_url: "" }));
    },
    [form.show_type],
  );

  const formError = useMemo(() => {
    if (!form.asset_name.trim()) return "Asset name is required.";
    if (!Number.isFinite(Number(form.yaw_deg))) return "Yaw must be a number.";
    if (!Number.isFinite(Number(form.pitch_deg))) return "Pitch must be a number.";
    const hasUrl = Boolean(form.content_url.trim());
    const hasUpload =
      Boolean(uploadFile) ||
      (hasStoredUpload && hasServerHotspotContent(form.content_url));
    if (form.show_type === "image" || form.show_type === "pdf") {
      if (!hasUpload) return "Upload a file is required.";
    } else if (form.show_type === "camera") {
      if (!hasUrl) return "Select a camera.";
    } else if (form.show_type === "text") {
      if (!hasUrl) return "Text content is required.";
      if (form.content_url.trim().length > HOTSPOT_TEXT_MAX_CHARS) {
        return `Text must be at most ${HOTSPOT_TEXT_MAX_CHARS} characters.`;
      }
    } else if (form.show_type === "api_url") {
      return validateApiUrlHotspotEntries(apiUrlEntries);
    } else if (!hasUrl) {
      return "Content URL is required.";
    }
    return null;
  }, [form, uploadFile, hasStoredUpload, apiUrlEntries]);

  const presetFormError = useMemo(() => {
    const normalizedPresetId = presetId.trim();
    if (!normalizedPresetId) return "Preset ID is required.";
    if (!/^\d+$/.test(normalizedPresetId)) {
      return `Preset ID must be a number from ${PRESET_ID_MIN} to ${PRESET_ID_MAX}.`;
    }
    const presetIdValue = Number(normalizedPresetId);
    if (presetIdValue < PRESET_ID_MIN || presetIdValue > PRESET_ID_MAX) {
      return `Preset ID must be between ${PRESET_ID_MIN} and ${PRESET_ID_MAX}.`;
    }
    if (!selectedGridId) return "Select a grid from the 360° preview.";
    return null;
  }, [presetId, selectedGridId]);

  const handleClose = () => {
    setMode("hotspot");
    setEditingId(null);
    setForm(EMPTY_FORM);
    clearUpload();
    clearApiUrlFields();
    setDeleteTarget(null);
    setDeleteError(null);
    setDeleteBusy(false);
    setPresetId("");
    setSelectedGridId(null);
    onClose();
  };

  const handleEdit = (h: CameraAssetHotspot) => {
    setEditingId(h.id);
    setForm(hotspotToForm(h));
    setUploadFile(null);
    setHasStoredUpload(hasServerHotspotContent(h.content_url));
    if (h.show_type === "api_url") {
      setApiUrlEntries(apiUrlHotspotEntriesFromContent(h.content_url));
    } else {
      clearApiUrlFields();
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    clearUpload();
    clearApiUrlFields();
  };

  const requestDeleteHotspot = (h: CameraAssetHotspot) => {
    setDeleteError(null);
    setDeleteTarget(h);
  };

  const closeDeleteHotspotDialog = () => {
    if (deleteBusy) return;
    setDeleteTarget(null);
    setDeleteError(null);
  };

  const confirmDeleteHotspot = async () => {
    if (!deleteTarget || deleteBusy) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await deleteCameraHotspot(cameraId, deleteTarget.id);
      await reload();
      if (editingId === deleteTarget.id) handleCancelEdit();
      setDeleteTarget(null);
      toast.success("Hotspot deleted.");
    } catch (e) {
      const message =
        e instanceof Error ? e.message : "Could not delete hotspot.";
      setDeleteError(message);
      toast.error(message);
    } finally {
      setDeleteBusy(false);
    }
  };

  const handleSave = async () => {
    if (formError || saving) return;
    setSaving(true);
    try {
      const payload: CameraAssetHotspotInput = {
        ...form,
        asset_name: form.asset_name.trim(),
        yaw_deg: Number(form.yaw_deg),
        pitch_deg: Number(form.pitch_deg),
        pin_direction_deg: Number(form.pin_direction_deg) || 0,
        content_url:
          form.show_type === "api_url"
            ? serializeApiUrlHotspotEntries(apiUrlEntries)
            : form.show_type === "website" ||
                form.show_type === "youtube" ||
                form.show_type === "camera" ||
                form.show_type === "text"
              ? form.content_url.trim()
              : form.content_url,
      };

      const fileForUpload =
        form.show_type === "image" || form.show_type === "pdf"
          ? uploadFile
          : null;

      await saveCameraHotspot(
        cameraId,
        payload,
        editingId ?? undefined,
        fileForUpload,
      );
      await reload();
      setEditingId(null);
      setForm(EMPTY_FORM);
      clearUpload();
      clearApiUrlFields();
      toast.success(editingId ? "Hotspot updated." : "Hotspot added.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not save hotspot.");
    } finally {
      setSaving(false);
    }
  };

  const handleSavePreset = async () => {
    if (presetFormError || savingPreset) return;
    const normalizedPresetId = presetId.trim();
    const gridId = selectedGridId as number;
    setSavingPreset(true);
    try {
      await saveCameraPreset({
        cameraid: cameraId,
        presetid: Number(normalizedPresetId),
        gridid: gridId,
      });
      await reloadPresets();
      toast.success("Preset mapping saved.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not save preset.");
    } finally {
      setSavingPreset(false);
    }
  };

  const handlePresetRowSelect = (row: CameraPreset) => {
    setPresetId(row.preset_id);
    setSelectedGridId(row.grid_id);
  };

  const title = cameraName
    ? mode === "preset"
      ? `Manage presets — ${cameraName}`
      : `Asset hotspots — ${cameraName}`
    : mode === "preset"
      ? "Manage presets"
      : "Asset hotspots";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
          borderBottom: "1px solid #E5E7EB",
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 600, color: "#101828" }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#6B7280", mt: 0.25 }}>
            Used in patrol 360° view.
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <CameraHotspotAnglePicker
          cameraId={cameraId}
          has360Stream={has360Stream}
          hasPtzStream={hasPtzStream}
          yawDeg={Number(form.yaw_deg) || 0}
          pitchDeg={Number(form.pitch_deg) || 0}
          pinDirectionDeg={Number(form.pin_direction_deg) || 0}
          savedHotspots={
            mode === "preset"
              ? []
              : hotspots.map((h) => ({
                  id: h.id,
                  yaw_deg: h.yaw_deg,
                  pitch_deg: h.pitch_deg,
                  label: h.asset_name,
                }))
          }
          activeHotspotId={mode === "preset" ? null : editingId}
          onSelectSavedHotspot={(id) => {
            if (mode === "preset") return;
            const h = hotspots.find((x) => x.id === id);
            if (h) handleEdit(h);
          }}
          onAnglesChange={({ yaw_deg, pitch_deg, pin_direction_deg }) =>
            setForm((p) => ({
              ...p,
              yaw_deg,
              pitch_deg,
              pin_direction_deg: pin_direction_deg ?? p.pin_direction_deg,
            }))
          }
          presetGridEnabled={mode === "preset"}
          selectedPresetGridId={selectedGridId}
          onPresetGridSelect={(gridId) => setSelectedGridId(gridId)}
        />

        {mode === "hotspot" ? (
          <>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#364153" }}>
              {editingId ? "Edit hotspot" : "Add hotspot"}
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1.5,
              }}
            >
              <TextField
                label="Asset name"
                size="small"
                required
                value={form.asset_name}
                onChange={(e) =>
                  setForm((p) => ({ ...p, asset_name: e.target.value }))
                }
              />
              <FormControl size="small">
                <InputLabel id="camera-hotspot-show-type">Show type</InputLabel>
                <Select
                  labelId="camera-hotspot-show-type"
                  label="Show type"
                  value={form.show_type}
                  onChange={(e) => {
                    const next = e.target.value as CameraAssetHotspotShowType;
                    setForm((p) => ({
                      ...p,
                      show_type: next,
                      content_url: next === p.show_type ? p.content_url : "",
                    }));
                    if (next !== "api_url") {
                      clearApiUrlFields();
                    } else if (next !== form.show_type) {
                      setApiUrlEntries(apiUrlHotspotEntriesFromContent(""));
                    }
                    clearUpload();
                  }}
                >
                  {CAMERA_ASSET_HOTSPOT_SHOW_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      {SHOW_TYPE_LABELS[t]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <HotspotFileUploadField
                showType={form.show_type}
                uploadFile={uploadFile}
                uploadPreviewUrl={uploadPreviewUrl}
                hasStoredUpload={hasStoredUpload}
                onPickFile={handlePickFile}
                onClearFile={clearUpload}
              />

              {(form.show_type === "image" || form.show_type === "pdf") &&
              !uploadFile &&
              !hasStoredUpload ? (
                <Typography
                  sx={{
                    gridColumn: { sm: "1 / -1" },
                    fontSize: 12,
                    color: "#6B7280",
                  }}
                >
                  {SHOW_TYPE_HINTS[form.show_type]}
                </Typography>
              ) : null}

              {form.show_type === "website" || form.show_type === "youtube" ? (
                <TextField
                  label="Content URL"
                  size="small"
                  required
                  fullWidth
                  sx={{ gridColumn: { sm: "1 / -1" } }}
                  value={form.content_url}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, content_url: e.target.value }))
                  }
                  helperText={SHOW_TYPE_HINTS[form.show_type]}
                />
              ) : null}

              {form.show_type === "text" ? (
                <TextField
                  label="Text content"
                  size="small"
                  required
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={8}
                  sx={{ gridColumn: { sm: "1 / -1" } }}
                  value={form.content_url}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, content_url: e.target.value }))
                  }
                  inputProps={{ maxLength: HOTSPOT_TEXT_MAX_CHARS }}
                  helperText={SHOW_TYPE_HINTS.text}
                />
              ) : null}

              {form.show_type === "api_url" ? (
                <AdminApiUrlHotspotFields
                  entries={apiUrlEntries}
                  onChange={setApiUrlEntries}
                />
              ) : null}

              {form.show_type === "camera" ? (
                <FormControl
                  size="small"
                  required
                  sx={{ gridColumn: { sm: "1 / -1" } }}
                >
                  <InputLabel id="camera-hotspot-linked-camera">
                    Select camera
                  </InputLabel>
                  <Select
                    labelId="camera-hotspot-linked-camera"
                    label="Select camera"
                    value={form.content_url}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, content_url: e.target.value }))
                    }
                    disabled={loadingExternalCameras}
                  >
                    {externalCameras.length === 0 ? (
                      <MenuItem value="" disabled>
                        {loadingExternalCameras
                          ? "Loading cameras…"
                          : "No external cameras found"}
                      </MenuItem>
                    ) : (
                      externalCameras.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  <Typography sx={{ fontSize: 12, color: "#6B7280", mt: 0.75 }}>
                    {SHOW_TYPE_HINTS.camera}
                  </Typography>
                </FormControl>
              ) : null}
            </Box>

            {formError ? (
              <Typography sx={{ fontSize: 13, color: "#B91C1C" }}>{formError}</Typography>
            ) : null}

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                disabled={Boolean(formError) || saving}
                onClick={() => void handleSave()}
                sx={{
                  textTransform: "none",
                  bgcolor: "#2932E5",
                  "&:hover": { bgcolor: "#1f28b8" },
                }}
              >
                {editingId ? "Update hotspot" : "Add hotspot"}
              </Button>
              {editingId ? (
                <Button
                  variant="outlined"
                  onClick={handleCancelEdit}
                  sx={{ textTransform: "none" }}
                >
                  Cancel edit
                </Button>
              ) : null}
            </Box>

            <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#364153", pt: 1 }}>
              Saved hotspots ({hotspots.length})
            </Typography>

            {loadingHotspots ? (
              <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                Loading hotspots…
              </Typography>
            ) : hotspots.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                No hotspots yet for this camera.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Yaw / Pitch</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {hotspots.map((h) => (
                    <TableRow key={h.id} hover selected={editingId === h.id}>
                      <TableCell>{h.asset_name}</TableCell>
                      <TableCell>
                        {formatSavedHotspotType(h.show_type)}
                      </TableCell>
                      <TableCell>
                        {h.yaw_deg}° / {h.pitch_deg}°
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          aria-label="Edit hotspot"
                          onClick={() => handleEdit(h)}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label="Delete hotspot"
                          color="error"
                          onClick={() => requestDeleteHotspot(h)}
                        >
                          <DeleteOutlinedIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        ) : (
          <>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#364153" }}>
              Manage presets
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#6B7280", mt: -1 }}>
              Default 36-grid ({PRESET_GRID_COLUMNS} columns × {PRESET_GRID_ROWS}{" "}
              rows) is shown on the view. Select any
              numbered grid, enter
              a preset ID, and save.
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1.5,
              }}
            >
              <TextField
                label="Preset ID"
                size="small"
                required
                value={presetId}
                onChange={(e) => setPresetId(e.target.value)}
                helperText={`Suggestion: use ${PRESET_ID_MIN}-${PRESET_ID_MAX}`}
              />
              <TextField
                label="Grid ID"
                size="small"
                required
                value={selectedGridId == null ? "" : String(selectedGridId)}
                InputProps={{ readOnly: true }}
                helperText="Auto-filled from selected grid"
              />
            </Box>
            {presetFormError ? (
              <Typography sx={{ fontSize: 13, color: "#B91C1C" }}>
                {presetFormError}
              </Typography>
            ) : null}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                disabled={Boolean(presetFormError) || savingPreset}
                onClick={() => void handleSavePreset()}
                sx={{
                  textTransform: "none",
                  bgcolor: "#2932E5",
                  "&:hover": { bgcolor: "#1f28b8" },
                }}
              >
                {savingPreset ? "Saving…" : "Save preset"}
              </Button>
            </Box>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#364153", pt: 1 }}>
              Saved presets ({cameraPresets.length})
            </Typography>
            {loadingPresets ? (
              <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                Loading presets…
              </Typography>
            ) : cameraPresets.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                No preset mappings saved yet for this camera.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Preset ID</TableCell>
                    <TableCell>Grid ID</TableCell>
                    <TableCell align="right">Saved</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cameraPresets.map((row) => (
                    <TableRow
                      key={row.preset_id}
                      hover
                      selected={presetId.trim() === row.preset_id}
                      onClick={() => handlePresetRowSelect(row)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>{row.preset_id}</TableCell>
                      <TableCell>{row.grid_id}</TableCell>
                      <TableCell align="right">
                        {new Date(row.updated_at || row.created_at).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={handleClose} sx={{ textTransform: "none" }}>
          Close
        </Button>
      </DialogActions>

      <DeletePatrolDrawingConfirmDialog
        open={Boolean(deleteTarget)}
        drawingTitle={deleteTarget?.asset_name ?? ""}
        loading={deleteBusy}
        error={deleteError}
        onClose={closeDeleteHotspotDialog}
        onConfirm={() => void confirmDeleteHotspot()}
      />
    </Dialog>
  );
}
